import { Link, useRecordContext, type RaRecord } from 'react-admin';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import { ModuleGate, companyIdOf } from '../ModuleGate';
import { showOrderSuggestions, useCarCard } from './CarCardContext';
import { carTodoItems, type TodoPlan, type TodoTone } from './carTodo';

/**
 * Panel „Do zrobienia” na karcie pojazdu (15.10.2026, makieta FTL
 * p. Mariusza 13.10, Etap 1 — decyzja Bartka 15.10.2026; przełącznik
 * `vehicleTodoPanel`). Reguły pozycji: `carTodo.ts`.
 *
 * Makieta stawia panel z prawej, obok harmonogramu. U nas stoi pod
 * podsumowaniem, na całą szerokość, pozycje obok siebie: oś przy 28 dniach
 * i tabela rekomendacji mieszczą się przy 1366 px tylko na pełnej szerokości
 * karty (decyzja 08.10.2026), a prawa kolumna zabrałaby im ok. 300 px.
 */

const TONE_COLOR: Record<TodoTone, (theme: Theme) => string> = {
  error: (t) => t.palette.error.main,
  warning: (t) => t.palette.warning.main,
  info: (t) => t.palette.info.main,
  ok: (t) => t.palette.success.main,
};

export const CarTodoPanel = ({ now = new Date() }: { now?: Date }) => {
  const r = useRecordContext<RaRecord & Record<string, unknown>>();
  const { overview } = useCarCard();
  if (!r) return null;
  const plan: TodoPlan = overview.plan
    ? { items: overview.plan.items }
    : overview.error
      ? { error: overview.error }
      : { loading: true };
  const items = carTodoItems(r, plan, now);
  return (
    <Card variant="outlined" data-testid="car-todo">
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Do zrobienia
        </Typography>
        <Box
          component="ul"
          sx={{
            m: 0,
            p: 0,
            listStyle: 'none',
            display: 'grid',
            gap: 1,
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
          }}
        >
          {items.map((item) => (
            <Box
              component="li"
              key={item.key}
              data-testid="car-todo-item"
              data-tone={item.tone}
              data-todo-key={item.key}
              sx={[
                { px: 1.5, py: 1, bgcolor: 'action.hover', borderRadius: 1, minWidth: 0 },
                (theme: Theme) => ({ borderLeft: `4px solid ${TONE_COLOR[item.tone](theme)}` }),
              ]}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {item.title}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {item.detail}
              </Typography>
              {item.orders && item.orders.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.5, mt: 0.5 }}>
                  {item.orders.map((o) => (
                    <Typography key={o.id} variant="caption" component="span">
                      <Link to={`/user-offers/${o.id}/show`}>{o.number}</Link>
                    </Typography>
                  ))}
                </Box>
              ) : null}
              {item.showSuggestions ? (
                // Rekomendacje to moduł spółki pojazdu — wyłączony: bez przycisku.
                <ModuleGate module="vehicleSuggestions" companyId={companyIdOf(r)}>
                <Button
                  size="small"
                  endIcon={<ArrowDownwardRoundedIcon />}
                  onClick={() => showOrderSuggestions()}
                  sx={{ mt: 0.5, px: 0.5 }}
                >
                  Pokaż rekomendowane zlecenia
                </Button>
                </ModuleGate>
              ) : null}
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};
